// Service portal cidadao do modulo PSS (Processo Seletivo Simplificado)
import { supabase } from '../lib/supabase';

export type StatusPSS =
  | 'rascunho' | 'aberto' | 'encerrado_inscricoes' | 'em_analise'
  | 'resultado_parcial' | 'resultado_final' | 'homologado' | 'cancelado';

export type StatusInscricao =
  | 'pendente' | 'classificada' | 'desclassificada' | 'aprovada' | 'reprovada';

export type Escolaridade =
  | 'fundamental' | 'medio' | 'tecnico' | 'superior_completo' | 'pos_graduacao';

export const STATUS_PSS_LABEL: Record<StatusPSS, string> = {
  rascunho: 'Rascunho',
  aberto: 'Inscrições abertas',
  encerrado_inscricoes: 'Inscrições encerradas',
  em_analise: 'Em análise',
  resultado_parcial: 'Resultado parcial',
  resultado_final: 'Resultado final',
  homologado: 'Homologado',
  cancelado: 'Cancelado',
};

export const STATUS_INSCRICAO_LABEL: Record<StatusInscricao, string> = {
  pendente: 'Pendente',
  classificada: 'Classificada',
  desclassificada: 'Desclassificada',
  aprovada: 'Aprovada',
  reprovada: 'Reprovada',
};

export const ESCOLARIDADE_LABEL: Record<Escolaridade, string> = {
  fundamental: 'Ensino Fundamental',
  medio: 'Ensino Médio',
  tecnico: 'Ensino Técnico',
  superior_completo: 'Superior Completo',
  pos_graduacao: 'Pós-Graduação',
};

export interface EditalPublico {
  id: string;
  numero: string;
  titulo: string;
  descricao: string | null;
  cargo: string;
  vagas: number | null;
  vagas_pcd: number | null;
  cadastro_reserva: number | null;
  escolaridade_minima: Escolaridade | null;
  requisitos: string | null;
  inscricoes_inicio: string | null;
  inscricoes_fim: string | null;
  data_prova: string | null;
  local_prova: string | null;
  valor_taxa: number | null;
  permite_isencao: boolean;
  edital_pdf_url: string | null;
  status_pss: StatusPSS;
  total_inscritos: number;
  data_abertura: string;
}

export interface MinhaInscricao {
  id: string;
  numero: string;
  data_abertura: string;
  status: string;
  status_inscricao: StatusInscricao;
  pontuacao: number | null;
  motivo_decisao: string | null;
  edital: {
    id: string;
    numero: string;
    titulo: string;
    cargo: string;
    status_pss: StatusPSS;
  };
}

export const pssPortalService = {
  async listarEditais(estabelecimentoId: string, status?: StatusPSS): Promise<EditalPublico[]> {
    const { data, error } = await supabase.rpc('pss_listar_editais_publicos', {
      p_estabelecimento_id: estabelecimentoId,
      p_status: status ?? null,
    });
    if (error) throw error;
    const r = data as { editais?: EditalPublico[]; erro?: string };
    return r.editais ?? [];
  },

  async buscarEdital(estabelecimentoId: string, numero: string): Promise<EditalPublico | null> {
    const todos = await this.listarEditais(estabelecimentoId);
    return todos.find((e) => e.numero === numero) ?? null;
  },

  async inscrever(args: {
    estabelecimento_id: string;
    cidadao_id: string;
    edital_id: string;
    titulo: string;
    candidato_nome: string;
    candidato_cpf: string;
    candidato_rg?: string;
    candidato_email: string;
    candidato_telefone?: string;
    pcd: boolean;
    escolaridade: Escolaridade;
    formacao: string;
    experiencia_anos?: number;
    experiencia_resumo?: string;
    isencao_solicitada?: boolean;
  }): Promise<{ id: string; numero: string }> {
    const { data, error } = await supabase
      .from('documentos')
      .insert({
        estabelecimento_id: args.estabelecimento_id,
        modulo_codigo: 'processo_seletivo',
        documento_pai_id: args.edital_id,
        titulo: args.titulo,
        descricao: `Inscrição de ${args.candidato_nome} no edital`,
        status: 'aberto',
        prioridade: 'media',
        visibilidade: 'interna',
        solicitante_cidadao_id: args.cidadao_id,
        campos_customizados: {
          tipo_pss: 'inscricao',
          candidato_nome: args.candidato_nome,
          candidato_cpf: args.candidato_cpf,
          ...(args.candidato_rg ? { candidato_rg: args.candidato_rg } : {}),
          candidato_email: args.candidato_email,
          ...(args.candidato_telefone ? { candidato_telefone: args.candidato_telefone } : {}),
          pcd: args.pcd,
          escolaridade: args.escolaridade,
          formacao: args.formacao,
          ...(args.experiencia_anos != null ? { experiencia_anos: args.experiencia_anos } : {}),
          ...(args.experiencia_resumo ? { experiencia_resumo: args.experiencia_resumo } : {}),
          ...(args.isencao_solicitada ? { isencao_solicitada: true } : {}),
        },
      })
      .select('id, numero')
      .single();
    if (error) throw error;
    return data as { id: string; numero: string };
  },

  async minhasInscricoes(): Promise<MinhaInscricao[]> {
    const { data, error } = await supabase.rpc('pss_minhas_inscricoes');
    if (error) throw error;
    const r = data as { inscricoes?: MinhaInscricao[]; erro?: string };
    return r.inscricoes ?? [];
  },
};
